import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { connect } from 'react-redux';

import kediOturan from 'assets/img/home/kedi-oturan.png';
import kediKosan from 'assets/img/home/kedi-kosan.png';
import {
  ik,
  ikSet,
  IK_HEIGHT_AT_900,
  APPSTORE_BADGE_SRC,
  PLAYSTORE_BADGE_SRC,
  STORE_LINKS,
  HERO_CHIPS,
  BOOK_COVERS,
  LEVELS,
  MODES,
  STUDENT_FEATURES,
  TEACHER_FEATURES,
  FAQ,
  QUICK_FACT,
} from './home-components/homeContent';
import SiteNav from './home-components/SiteNav';
import JsonLd from 'seo/JsonLd';
import usePageMeta from 'seo/usePageMeta';
import { organizationLd, websiteLd, appsLd, faqLd } from 'seo/site';
import PhonicsShowcase from './home-components/PhonicsShowcase';
import SiteFooter from './home-components/SiteFooter';
import {
  isLoggedInUser,
  isTeacherUser,
  teacherPanelUrlFor,
} from './home-components/authLinks';

const CONSENT_KEY = 'PrivacyAccepted';

const StoreBadges = ({ links, label }) => (
  <div className="op-badges">
    <a href={links.appStore} target="_blank" rel="noreferrer">
      <img src={APPSTORE_BADGE_SRC} alt={`${label} - App Store'dan indir`} />
    </a>
    <a href={links.googlePlay} target="_blank" rel="noreferrer">
      <img src={PLAYSTORE_BADGE_SRC} alt={`${label} - Google Play'den indir`} />
    </a>
  </div>
);

const SectionHead = ({ id, kicker, title, text, tone }) => (
  <div className="op-sec-head">
    <span className={`op-kicker${tone ? ` op-kicker--${tone}` : ''}`}>
      {kicker}
    </span>
    <h2 id={id}>{title}</h2>
    {text ? <p>{text}</p> : null}
  </div>
);

// Must match the max-height of `.op-feature__media img` per card size in _home-v2.scss.
const FEATURE_MAX_HEIGHT = { base: 300, wide: 340, tall: 480, full: 320 };
const APP_SHOT_HEIGHT = 220;

// Contained screenshots are capped by height, so maxHeight * aspect ratio is the
// widest they are ever drawn. Serving that width (2x for retina) instead of a fixed
// 900px keeps the layout identical while cutting the bytes on 1x screens.
const ikFixedHeight = (name, maxHeight) => {
  const width = Math.ceil((maxHeight * 900) / IK_HEIGHT_AT_900[name]);
  return {
    src: ik(name, 900),
    srcSet: ikSet(name, [width, width * 2]),
    sizes: `${width}px`,
    width,
    height: maxHeight,
  };
};

// Cover photos fill the card, whose width depends on the grid columns.
const ikCover = (name) => ({
  src: ik(name, 900),
  srcSet: ikSet(name, [360, 640, 900, 1280]),
  sizes:
    '(max-width: 680px) calc(100vw - 76px), (max-width: 980px) calc(50vw - 63px), 360px',
  width: 900,
  height: IK_HEIGHT_AT_900[name],
});

const FeatureCard = ({ feature }) => (
  <article
    className={`op-feature op-feature--${feature.size || 'base'}`}
    style={{ '--tint': feature.tint }}
  >
    <div className="op-feature__body">
      <h3>{feature.title}</h3>
      <p>{feature.text}</p>
      {feature.list ? (
        <ul className="op-checks">
          {feature.list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
    </div>
    <div
      className={`op-feature__media${
        feature.fit === 'cover' ? ' op-feature__media--cover' : ''
      }`}
    >
      <img
        {...(feature.fit === 'cover'
          ? ikCover(feature.image)
          : ikFixedHeight(feature.image, FEATURE_MAX_HEIGHT[feature.size || 'base']))}
        alt={feature.title}
        loading="lazy"
      />
    </div>
  </article>
);

const Home = ({ localizations, currentUser, token }) => {
  const history = useHistory();

  const [showConsent, setShowConsent] = useState(true);
  const [shelfPaused, setShelfPaused] = useState(false);

  usePageMeta('/');

  useEffect(() => {
    const item = localStorage.getItem(CONSENT_KEY);

    if (item && item !== '') {
      const expiry = parseInt(item);

      if (Date.now() > expiry) {
        localStorage.removeItem(CONSENT_KEY);
      } else {
        setShowConsent(false);
      }
    }
  }, []);

  // Smooth anchor scrolling only while the landing page is mounted, so other
  // pages' programmatic scrolls keep their default behaviour.
  useEffect(() => {
    document.documentElement.classList.add('op-smooth');
    return () => document.documentElement.classList.remove('op-smooth');
  }, []);

  useEffect(() => {
    if (currentUser?.id > 0 && currentUser?.userType === 'STUDENT') {
      history.push('/student');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const consentAcceptClicked = (e) => {
    e.preventDefault();
    const ttl = 1000 * 60 * 60 * 24;
    localStorage.setItem(CONSENT_KEY, (Date.now() + ttl).toString());
    setShowConsent(false);
  };

  const isLoggedIn = isLoggedInUser(currentUser);
  const isTeacher = isTeacherUser(currentUser);
  const teacherPanelUrl = teacherPanelUrlFor(token);

  // One shelf "set" is the covers twice, so a set is always wider than the
  // viewport; the second, aria-hidden set makes the -50% loop seamless.
  const shelfSet = [...BOOK_COVERS, ...BOOK_COVERS];

  return (
    <div className="op-home">
      <a className="op-skip" href="#icerik">
        İçeriğe geç
      </a>

      <SiteNav />

      <main id="icerik">
        {/* HERO */}
        <section className="op-wrap op-hero">
          <div className="op-hero__copy">
            <a className="op-announce" href="#ses-bilgisi">
              <span className="op-announce__tag">Yeni</span>
              Ses Bilgisi ile ses temelli okuma →
            </a>
            <h1>
              Okumanın en eğlenceli hâli,{' '}
              <em>tüm cihazlarda.</em>
            </h1>
            <p className="op-hero__sub">
              Kerem, Bezgin ve Çengel'in maceraları seni bekliyor! 200+
              hikâyeden oluşan dijital, sesli kitaplar; öğretmenler için görev,
              materyal ve rapor araçları. 7/24 çevrim içi.
            </p>
            <div className="op-hero__cta">
              {isTeacher ? (
                <a className="op-btn op-btn--primary op-btn--lg" href={teacherPanelUrl}>
                  Öğretmen Paneline Git →
                </a>
              ) : isLoggedIn ? null : (
                <a className="op-btn op-btn--primary op-btn--lg" href="/signin">
                  Giriş Yap →
                </a>
              )}
              <a className="op-btn op-btn--accent op-btn--lg" href="/buy-now">
                Okulunuz için teklif alın
              </a>
            </div>
            <ul className="op-chips">
              {HERO_CHIPS.map((chip) => (
                <li key={chip}>{chip}</li>
              ))}
            </ul>
          </div>

          <div className="op-hero__visual">
            <span className="op-blob op-blob--sun" aria-hidden="true" />
            <img
              className="op-hero__pc"
              src={ik('pc2', 1200)}
              srcSet={ikSet('pc2', [500, 700, 900, 1200])}
              sizes="(max-width: 980px) 90vw, 520px"
              alt="Okuma Platformu web okuma ekranı"
              fetchpriority="high"
            />
            <img
              className="op-hero__ipad"
              src={ik('ipad', 700)}
              srcSet={ikSet('ipad', [350, 500, 700])}
              sizes="(max-width: 980px) 42vw, 260px"
              alt="Okuma Platformu öğrenci uygulaması iPad ekranı"
            />
            <img
              className="op-hero__cat"
              src={kediOturan}
              alt="Okuma Platformu'nun kedisi Çengel ekrana bakıyor"
            />
          </div>
        </section>

        {/* LOGIN ROLES */}
        <section className="op-wrap op-roles" id="giris" aria-labelledby="giris-baslik">
          <h2 id="giris-baslik" className="op-visually-hidden">
            Giriş seçenekleri
          </h2>
          <article className="op-role op-role--student">
            <span className="op-role__icon" aria-hidden="true">🎒</span>
            <h3>Öğrenci</h3>
            <p>
              Okulunun verdiği kullanıcı adı ve şifreyle giriş yap; kitapların
              ve görevlerin seni bekliyor.
            </p>
            <a className="op-btn op-btn--primary" href="/signin">
              Öğrenci Girişi
            </a>
            <span className="op-role__apps">Tablet ve telefonda: Okuma Platformu Okul</span>
            <StoreBadges links={STORE_LINKS.okul} label="Okuma Platformu Okul" />
          </article>
          <article className="op-role op-role--teacher">
            <span className="op-role__icon" aria-hidden="true">🍎</span>
            <h3>Öğretmen ve Okul Yöneticisi</h3>
            <p>
              Aynı ekrandan giriş yapın; hesabınız sizi otomatik olarak
              öğretmen paneline yönlendirir.
            </p>
            <a className="op-btn op-btn--violet" href={isTeacher ? teacherPanelUrl : '/signin'}>
              Öğretmen Girişi
            </a>
          </article>
          <article className="op-role op-role--family">
            <span className="op-role__icon" aria-hidden="true">🏡</span>
            <h3>Veli ve Bireysel</h3>
            <p>
              Okul hesabı olmadan, evde okumak için Okuma Platformu Bireysel
              uygulamasını indirin.
            </p>
            <span className="op-role__apps">Okuma Platformu Bireysel</span>
            <StoreBadges links={STORE_LINKS.bireysel} label="Okuma Platformu Bireysel" />
          </article>
          <p className="op-roles__note">
            Şifrenizi mi unuttunuz?{' '}
            <a href="/forgot-password">Yeni şifre oluşturun</a>
          </p>
        </section>

        {/* SHELF */}
        <section className="op-shelf" aria-labelledby="raf-baslik">
          <div className="op-wrap">
            <SectionHead
              id="raf-baslik"
              kicker="Okuma Ağacı kütüphanesinden"
              title="Çocukların açmak için sabırsızlandığı 200+ hikâye"
            />
          </div>
          <div className="op-shelf__rail">
            <button
              type="button"
              className="op-marquee__toggle"
              aria-pressed={shelfPaused}
              aria-label={shelfPaused ? 'Kitap rafını oynat' : 'Kitap rafını durdur'}
              onClick={() => setShelfPaused(!shelfPaused)}
            >
              {shelfPaused ? '▶' : '❚❚'}
            </button>
            <div className={`op-marquee${shelfPaused ? ' op-marquee--paused' : ''}`}>
              <ul>
                {shelfSet.map((book, i) => (
                  <li key={`a-${i}`}>
                    <img src={book.src} alt={book.title} loading="lazy" />
                  </li>
                ))}
                {shelfSet.map((book, i) => (
                  <li key={`b-${i}`} aria-hidden="true">
                    <img src={book.src} alt="" loading="lazy" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* QUICK FACT */}
        <section className="op-wrap op-fact-wrap">
          <p className="op-fact">
            <strong>Okuma Platformu</strong>, {QUICK_FACT}
          </p>
        </section>

        {/* SES BILGISI */}
        <section className="op-wrap op-phonics" id="ses-bilgisi">
          <PhonicsShowcase />
        </section>

        {/* OKUMA AGACI */}
        <section className="op-wrap op-tree" id="okuma-agaci">
          <div className="op-tree__art">
            <img
              src={ik('oka', 760)}
              srcSet={ikSet('oka', [400, 600, 760])}
              sizes="(max-width: 480px) 80vw, 400px"
              alt="Okuma Ağacı: dallarında 1'den 10'a seviye yuvarlakları, altında kedi Çengel"
              loading="lazy"
            />
          </div>
          <div className="op-tree__copy">
            <span className="op-kicker op-kicker--green">Okuma Ağacı</span>
            <h2>Her çocuk kendi dalından başlar</h2>
            <p>
              Okuma Ağacı, okul öncesi ve ilkokul öğrencilerine yönelik 13
              seviyeye ayrılmış, 200+ hikâyeyi içeren okuma serisidir.
            </p>
            <p>
              Yaşamın içinden, farklı beceri seviyelerine uygun güçlü görsel
              anlatım. Çocuk seviyesini tamamladıkça bir üst dala tırmanır.
            </p>
            <ul className="op-levels" aria-label="Okuma seviyeleri">
              {LEVELS.map((level) => (
                <li
                  key={level.label}
                  style={{ background: level.color, color: level.ink }}
                >
                  {level.label}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* MODES */}
        <section className="op-modes-band">
          <div className="op-wrap">
            <SectionHead
              kicker="Her kitapta beş yol"
              title="Oku. Dinle. Kaydet. Değerlendir."
              text="Her hikâye, çocuğun okuma becerisini farklı bir yönden geliştirir."
            />
            <ul className="op-modes">
              {MODES.map((mode) => (
                <li key={mode.title}>
                  <img src={mode.image} alt="" loading="lazy" />
                  <h3>{mode.title}</h3>
                  <p>{mode.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* STUDENT */}
        <section className="op-wrap op-section" id="ogrenci">
          <SectionHead
            kicker="Öğrenciler için"
            tone="orange"
            title="Okurken dinle, kaydet, keşfet"
            text="Çocukların kendi başına kullanabileceği, sade ve renkli bir okuma deneyimi."
          />
          <div className="op-bento">
            {STUDENT_FEATURES.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </div>
        </section>

        {/* TEACHER */}
        <section className="op-teacher-band" id="ogretmen">
          <div className="op-wrap op-section">
            <SectionHead
              kicker="Öğretmenler için"
              tone="violet"
              title="Sınıfın okuma yolculuğu tek panelde"
              text="Görev verin, kayıtları dinleyin, gelişimi raporlarla izleyin."
            />
            <div className="op-bento">
              {TEACHER_FEATURES.map((feature) => (
                <FeatureCard key={feature.title} feature={feature} />
              ))}
            </div>
            <div className="op-center">
              <a className="op-btn op-btn--violet op-btn--lg" href={isTeacher ? teacherPanelUrl : '/signin'}>
                Öğretmen Girişi →
              </a>
            </div>
          </div>
        </section>

        {/* APPS */}
        <section className="op-wrap op-section" id="uygulamalar">
          <SectionHead
            kicker="Uygulamalar"
            title="Okulda, evde, yolda"
            text="Web tarayıcısından ya da iPad, iPhone ve Android uygulamalarından; tüm cihazlarda 7/24."
          />
          <div className="op-apps">
            <article className="op-app">
              <img className="op-app__shot" {...ikFixedHeight('iphone', APP_SHOT_HEIGHT)} alt="Okuma Platformu Okul uygulaması kitap rafı" loading="lazy" />
              <h3>Okuma Platformu Okul</h3>
              <p>Okulunuzun verdiği hesapla giriş yapın; kitaplar, görevler ve mesajlar her cihazda sizinle.</p>
              <StoreBadges links={STORE_LINKS.okul} label="Okuma Platformu Okul" />
            </article>
            <article className="op-app op-app--family">
              <img className="op-app__shot" {...ikFixedHeight('ipad_pro_11_home', APP_SHOT_HEIGHT)} alt="Okuma Platformu Bireysel uygulaması ana ekranı" loading="lazy" />
              <h3>Okuma Platformu Bireysel</h3>
              <p>Okul hesabı olmayan aileler için: evde, kendi hızınızda okuma keyfi.</p>
              <StoreBadges links={STORE_LINKS.bireysel} label="Okuma Platformu Bireysel" />
            </article>
          </div>
        </section>

        {/* FAQ */}
        <section className="op-wrap op-section" id="sss">
          <SectionHead kicker="Sık sorulan sorular" title="Merak edilenler" />
          <div className="op-faq">
            {FAQ.map((item, i) => (
              <details key={item.q} open={i === 0}>
                <summary>
                  <h3>{item.q}</h3>
                  <span className="op-faq__chev" aria-hidden="true">▾</span>
                </summary>
                <p>
                  {item.a}{' '}
                  {item.link ? <a href={item.link.href}>{item.link.label} →</a> : null}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CLOSING */}
        <section className="op-wrap op-closing">
          <img className="op-closing__cat" src={kediKosan} alt="" aria-hidden="true" />
          <div className="op-closing__card">
            <h2>Bugün okunan hikâye, en sevilen hikâye olabilir.</h2>
            <p>Okulunuzu Okuma Platformu ile tanıştırın; size özel teklifimizi hazırlayalım.</p>
            <div className="op-hero__cta op-hero__cta--center">
              <a className="op-btn op-btn--accent op-btn--lg" href="/buy-now">
                Teklif İste
              </a>
              <a className="op-btn op-btn--ghost op-btn--lg" href="/contact">
                Bize Ulaşın
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      <JsonLd data={[organizationLd(), websiteLd(), ...appsLd(), faqLd(FAQ)]} />

      {showConsent ? (
        <div className="consent" role="dialog" aria-live="polite" aria-label="Çerez bildirimi">
          <div className="consent__text">
            <strong>
              {localizations?.HOME_CONSENT_TITLE ?? 'Lütfen kullanım koşullarını inceleyiniz.'}
            </strong>
            <p>
              {localizations?.HOME_CONSENT_TEXT ??
                'Bilgi toplamak ve siteyi kişiselleştirmek için çerezler ve benzeri teknolojiler kullanıyoruz. Bu siteyi kullanmaya devam ederek, bu çerezlerin ve benzeri gizlilik teknolojilerinin uygulamalarımıza yerleştirilmesine izin vermiş olursunuz.'}{' '}
              {localizations?.HOME_CONSENT_FOOTER_PART1 ?? 'Daha fazla bilgi edinmek için lütfen'}{' '}
              <a href="/terms-of-use">
                {localizations?.HOME_CONSENT_FOOTER_PART2 ?? 'Kullanım Koşulları'}
              </a>
              {localizations?.HOME_CONSENT_FOOTER_PART3 ?? "'nı okuyunuz."}
            </p>
          </div>
          <button type="button" className="op-btn op-btn--primary" onClick={consentAcceptClicked}>
            {localizations?.HOME_CONSENT_ACCEPT_BTN ?? 'Kabul Et'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.auth.currentUser,
  token: state.auth.token,
  localizations: state.localization.pairs,
});

export default connect(mapStateToProps)(Home);
